import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { Permissions } from "../../../../helpers/constants";
import { getMenuIcon } from "../../../../helpers/theme/getMenuIcon";
import useMenuAction from "../../../../hooks/menu/useMenuAction";
import useMenuData from "../../../../hooks/menu/useMenuData";
import { NAVIGATION } from "../../../../locales/components/namespaces";
import PermissionHOC from "../../../../modules/permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../../../modules/permission/hooks/useHasPermission";
import { MenuOrientation } from "../../../../themes/config";
import useConfig from "../../../../themes/hooks/useConfig";

// project import

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level, onClickAction, isAction = false }) => {
	const {t} = useTranslation(NAVIGATION);
	const theme = useTheme();
	const { menuOrientation } = useConfig();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const { drawerOpen, openItem } = useMenuData()
	const { setMenuActiveItem, setDrawerOpen, } = useMenuAction()
	const [isHovered, setIsHovered] = useState(false);
	const closeRef = useRef()
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	let itemTarget = '_self';
	if (item.target) {
		itemTarget = '_blank';
	}
	let listItemProps = {};
	
	if(!isAction) {
		if (item?.external) {
			listItemProps = { component: 'a', href: item.url, target: itemTarget };
		} else {
			listItemProps = {
				component: forwardRef((props, ref) => <Link {...props} to={item.url} target={itemTarget} ref={ref}/>)
			};
		}
	}
	const Icon = getMenuIcon(item.icon)
	const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }}/> : false;
	
	const pathname = document.location.pathname;
	const urlParams = document.location.search;
	const isSelected = openItem.findIndex((id) => id === item.id) > -1 || (!openItem && urlParams.includes('faqId')) || (!item.id && urlParams.includes('faqId'))
	// active menu item on page load
	useEffect(() => {
		if ((`${pathname}${urlParams}`=== item.url) && !isAction && openItem[0] !== item.id) {
			setMenuActiveItem({ openItem: [item.id] });
		}
		if(urlParams.includes('faqId') && !isOrganizer) {
			setMenuActiveItem({ openItem: ['group-dashboard'] });
		}
		// eslint-disable-next-line
	}, [pathname, urlParams]);

	const textColor = 'highlight.contrastText';
	
	const checkHover = useCallback((e => {
		if (closeRef.current) {
			const mouseOver = closeRef.current.contains(e.target);
			if (!isHovered && mouseOver) {
				//console.log(isHovered, mouseOver);
				setIsHovered(true)
			}
			
			if (isHovered && !mouseOver) {
				setIsHovered(false)
			}
		}
	}), [isHovered])
	
	useEffect(() => {
		window.addEventListener("mousemove", checkHover, true);
		return () => {
			window.removeEventListener("mousemove", checkHover, true);
		}
	}, [checkHover, isHovered])
	const isNew = useMemo(() => (item?.isNew), [item?.isNew])
	const navItem = (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				//Μόνο εδώ βλέπω να γίνεται αλλαγή
				<ListItemButton
					{...listItemProps}
					ref={closeRef}
					onMouseEnter={(e) => {
						e.preventDefault();
						setIsHovered(true)
					}}
					onMouseLeave={(e) => {
						e.preventDefault();
						setIsHovered(false)
					}}
					disabled={item.disabled}
					selected={isSelected}
					sx={{
						mb: isOrganizer ? '2rem' : 4,
						zIndex: 1201,
						borderRadius: '12px',
						marginBottom:'16px',
						background: theme.palette.highlight.main,
						...(drawerOpen && {
							display: 'flex',
							padding: '16px 32px 16px 32px',
							gap: '6px',
							maxHeight: '56px',
							'&:hover': {
								borderRadius: '12px',
								background: theme.palette.highlight.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								h6: {
									color: '#fff'
								}
							},
							'&.Mui-selected': {
								borderRadius: '12px',
								background: theme.palette.highlight.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								h6: {
									color: '#fff'
								},
								'&:hover': {
									borderRadius: '12px',
									background: theme.palette.highlight.dark,
									boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
									
									h6: {
										color: '#fff'
									}
								}
							},
						}),
						...(!drawerOpen && {
							display: 'flex',
							padding: '10px',
							alignItems: 'center',
							justifyContent: 'center',
							maxWidth: '44px',
							'&:hover': {
								background: theme.palette.highlight.dark,
							},
							'&.Mui-selected': {
								background: theme.palette.highlight.dark,
								'&:hover': {
									background: theme.palette.highlight.dark,
								}
							}
						})
					}}
					onClick={(event) => {
						event.stopPropagation()
						if(item.id) setMenuActiveItem({ openItem: [item.id] })
						if (downLG) setDrawerOpen(false);
						if(isAction) onClickAction()
					}}
					title={item.hoverMessage}
				>
					<ListItemIcon
						sx={{
							minWidth: 24
						}}
					>
						{itemIcon}
					</ListItemIcon>
					{(drawerOpen || (!drawerOpen && level !== 1)) && (
						<ListItemText
							primary={
								<Typography
									variant="body1"
									sx={{
										color: isSelected ? textColor : textColor,
										textWrap: 'wrap',
									}}
								>
									{isNew ? <span>{item.title}<sup style={{
										color: '#d32f2f',
										paddingLeft: '4px',
										fontWeight: 'bold'
									}}>{t('NEW')}</sup></span> : item.title}
								</Typography>
							}
						/>
					)}
					{(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
						<Chip
							color={item.chip.color}
							variant={item.chip.variant}
							size={item.chip.size}
							label={item.chip.label}
							avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
						/>
					)}
				</ListItemButton>
			) : null}
		</>
	)
	return item.permissions && item.permissions.length > 0 ? (
		<PermissionHOC
			requiredPermissions={[...item.permissions]}>
			{navItem}
		</PermissionHOC>
	) : (
		<>{navItem}</>
	)
}
//
// NavItem.propTypes = {
//     item: PropTypes.object,
//     level: PropTypes.number
// };

export default NavItem;
